import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Badge,
  InputAdornment,
  OutlinedInput,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Collapse,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  FormHelperText,
  Skeleton
} from '@mui/material';
import {
  BusinessCenter as JobIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  VisibilityOff as HideIcon,
  Visibility as ShowIcon,
  Add as AddIcon,
  LocationOn as LocationIcon,
  AddLocation as AddLocationIcon,
  DeleteOutline as RemoveLocationIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  CheckCircle as ReceivedIcon,
  Schedule as PendingIcon
} from '@mui/icons-material';
import { useJob } from '../contexts/JobContext';
import { useInventory } from '../contexts/InventoryContext';
import { useInventoryPage } from '../contexts/InventoryPageContext';
import { equipmentApi, planningApi } from '../services/api.service';
import api from '../services/api.service';
import RecentActivity from '../components/RecentActivity';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

export default function JobsPage() {
  const navigate = useNavigate();
  const hasPMAccess = useFeatureFlag('pm_demo');
  const {
    jobList,
    currentJob,
    loading,
    error,
    loadJobs,
    createJob,
    updateJob,
    deleteJob,
    clearError
  } = useJob();
  
  const { inventorySummary, loadInventorySummary } = useInventory();
  const { warehouses: allWarehouses, racks: allRacks, bays: allBays } = useInventoryPage();

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [dialogTab, setDialogTab] = useState(0); // 0: Basic only
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetails, setJobDetails] = useState({
    name: '',
    client: '',
    location: '',
    startDate: '',
    endDate: '',
    notes: '',
    status: 'Planning',
    sites: [],
    customJobId: ''
  });
  const [useAutoId, setUseAutoId] = useState(false);
  const [idError, setIdError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  
  // Load filter settings from localStorage
  const loadFilterSettings = () => {
    const savedSettings = localStorage.getItem('jobsPageFilters');
    if (savedSettings) {
      try {
        return JSON.parse(savedSettings);
      } catch (e) {
        console.error('Failed to parse saved filter settings:', e);
      }
    }
    return null;
  };

  const savedFilters = loadFilterSettings();

  // Filtering and search
  const [searchQuery, setSearchQuery] = useState(savedFilters?.searchQuery || '');
  const [statusFilters, setStatusFilters] = useState(savedFilters?.statusFilters || {
    'Needs Planning': true,
    'Awaiting Equipment': true,
    'Receiving Equipment': true,
    'Ready to Install': true,
    'In Progress': true,
    'Completed': true
  });
  const [filteredJobList, setFilteredJobList] = useState([]);
  const [showFilters, setShowFilters] = useState(savedFilters?.showFilters || false);
  const [jobEquipmentStats, setJobEquipmentStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);

  // Date filtering
  const [dateFilter, setDateFilter] = useState(savedFilters?.dateFilter || 'all');
  const [customDateRange, setCustomDateRange] = useState(savedFilters?.customDateRange || {
    startDate: '',
    endDate: ''
  });
  
  // Stack location selection for job creation
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [selectedRack, setSelectedRack] = useState('');
  const [selectedBay, setSelectedBay] = useState('');
  const [stackLocations, setStackLocations] = useState([]);
  const [showAddLocationForm, setShowAddLocationForm] = useState(false);
  const [locationWarehouseFilters, setLocationWarehouseFilters] = useState({});
  
  // Note: allRacks and allBays come from InventoryPageContext
  
  // Site management

  // Handle input changes for job form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setJobDetails(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Validate custom job ID if changed
    if (name === 'customJobId') {
      validateJobId(value);
    }
  };
  
  // Validate custom job ID
  const validateJobId = (id) => {
    setIdError('');
    
    if (!id) {
      setIdError('Job ID is required');
      return false;
    }
    
    // Check if ID contains invalid characters
    if (!/^[A-Za-z0-9\-_]+$/.test(id)) {
      setIdError('ID can only contain letters, numbers, hyphens, and underscores');
      return false;
    }
    
    // Check if ID is already in use by another job
    const existingJob = jobList.find(job => (job.jobNumber || job.id) === id);
    if (existingJob && (dialogMode === 'create' || (dialogMode === 'edit' && selectedJob?.id !== existingJob.id))) {
      setIdError('This job number is already in use');
      return false;
    }
    
    return true;
  };

  // Generate a preview of the auto-generated ID
  const generatePreviewId = () => {
    return `J${(jobList.length + 1).toString().padStart(3, '0')}`;
  };
  
  // Open dialog to create a new job
  const handleOpenCreateDialog = () => {
    setDialogMode('create');
    setDialogTab(0);
    setJobDetails({
      name: '',
      client: '',
      location: '',
      startDate: '',
      endDate: '',
      notes: '',
      status: 'Planning',
      sites: [],
      customJobId: '',
      priority: 'Normal'
    });
    setUseAutoId(false);
    setIdError('');
    // Reset stack location selection
    setSelectedWarehouse('');
    setSelectedRack('');
    setSelectedBay('');
    setShowAddLocationForm(false);
    setLocationWarehouseFilters({});
    setOpenDialog(true);
  };

  // Open dialog to edit a job
  const handleOpenEditDialog = (job) => {
    setDialogMode('edit');
    setDialogTab(0);
    setSelectedJob(job);
    setJobDetails({
      name: job.name,
      client: job.client,
      location: job.location || '',
      startDate: job.startDate || '',
      endDate: job.endDate || '',
      notes: job.notes || '',
      status: job.status || 'Planning',
      sites: job.sites || [],
      // Store the current job ID for editing
      customJobId: job.jobNumber || job.id || '',
      priority: job.priority || 'Normal'
    });
    // Allow editing the job ID
    setUseAutoId(false);
    setIdError('');
    
    // Parse existing stack locations if available
    if (job.stackLocations && job.stackLocations.length > 0) {
      setStackLocations(job.stackLocations);
    } else {
      setStackLocations([]);
    }
    // Reset single location selectors
    setSelectedWarehouse('');
    setSelectedRack('');
    setSelectedBay('');
    setShowAddLocationForm(false);

    // Clear location filters when opening for edit
    setLocationWarehouseFilters({});

    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedJob(null);
    setDialogTab(0);
    // Reset stack location selection
    setSelectedWarehouse('');
    setSelectedRack('');
    setSelectedBay('');
    setStackLocations([]);
    setShowAddLocationForm(false);
    setLocationWarehouseFilters({});
  };

  // Save job (create or update)
  const handleSaveJob = async () => {
    // Basic validation
    if (!jobDetails.name || !jobDetails.client) {
      return;
    }
    
    // Validate job ID
    const jobId = dialogMode === 'create' && useAutoId 
      ? generatePreviewId() 
      : jobDetails.customJobId;
    
    if (!validateJobId(jobId)) {
      return; // Don't proceed if the job ID is invalid
    }

    setDialogLoading(true);
    try {
      // Prepare job data to send
      const jobDataToSend = {
        name: jobDetails.name,
        client: jobDetails.client,
        location: jobDetails.location,
        startDate: jobDetails.startDate,
        endDate: jobDetails.endDate,
        status: jobDetails.status,
        notes: jobDetails.notes,
        sites: jobDetails.sites,
        customJobId: jobDetails.customJobId,
        stackLocations: stackLocations || []
        // TODO: Add priority back once database migration is run
        // priority: jobDetails.priority || 'Normal'
      };
      
      console.log('Job data being sent:', JSON.stringify(jobDataToSend, null, 2));
      console.log('Stack locations:', stackLocations);
      
      if (dialogMode === 'create') {
        await createJob(jobDataToSend);
      } else {
        await updateJob(selectedJob.id, jobDataToSend);
        // Refresh the job list to ensure UI is up to date
        await loadJobs();
      }
      handleCloseDialog();
    } catch (err) {
      console.error('Failed to save job:', err);
      // Extract detailed error message
      let errorMessage = 'Failed to save job';
      console.log('Full error response:', err.response);
      if (err.response?.data?.validationErrors) {
        // Handle validation errors
        const errors = err.response.data.validationErrors;
        if (Array.isArray(errors)) {
          errorMessage = errors.map(e => `${e.field}: ${e.message}`).join('; ');
        } else {
          errorMessage = Object.entries(errors).map(([field, msgs]) => 
            `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`
          ).join('; ');
        }
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setIdError(errorMessage);
    } finally {
      setDialogLoading(false);
    }
  };






  // Open delete confirmation dialog
  const handleOpenDeleteConfirm = (job) => {
    setJobToDelete(job);
    setShowDeleteConfirm(true);
  };

  // Close delete confirmation dialog
  const handleCloseDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setJobToDelete(null);
  };

  // Delete a job
  const handleDeleteJob = async () => {
    if (!jobToDelete) return;

    try {
      await deleteJob(jobToDelete.id);
      handleCloseDeleteConfirm();
    } catch (err) {
      console.error('Failed to delete job:', err);
    }
  };



  // Load equipment stats for all jobs - OPTIMIZED: Single batch request
  const loadJobEquipmentStats = useCallback(async () => {
    if (!jobList || jobList.length === 0) return;

    setStatsLoading(true);
    try {
      // Use new batch endpoint for all job stats in one request
      const jobIds = jobList.map(job => job.id).join(',');
      const response = await api.get(`/jobs/equipment-stats?jobIds=${jobIds}`);

      if (response.data?.data) {
        setJobEquipmentStats(response.data.data);
      } else {
        // Fallback to individual requests if batch endpoint fails
        const statsPromises = jobList.map(async (job) => {
          try {
            // Get planned equipment - this includes received and installed counts
            let plannedItems = 0;
            let receivedItems = 0;
            let installedItems = 0;
            let hasStockShortage = false;

            try {
              const plannedResponse = await planningApi.getJobEquipmentPlans(job.id);
              const plans = plannedResponse.data?.data || plannedResponse.data || [];

              // Sum up quantities from all plans
              plannedItems = plans.reduce((sum, plan) => sum + (plan.needed_quantity || plan.neededQuantity || 0), 0);
              receivedItems = plans.reduce((sum, plan) => sum + (plan.assigned_quantity || plan.assignedQuantity || 0), 0);
              installedItems = plans.reduce((sum, plan) => sum + (plan.installed_quantity || plan.installedQuantity || 0), 0);

              // Check if any planned items have stock shortage
              hasStockShortage = plans.some(plan => {
                const needed = plan.needed_quantity || plan.neededQuantity || 0;
                const received = plan.assigned_quantity || plan.assignedQuantity || 0;
                return needed > 0 && received < needed;
              });
            } catch (planningError) {
              // If planning API fails, just use 0 for all counts
              plannedItems = 0;
              receivedItems = 0;
              installedItems = 0;
              hasStockShortage = false;
            }

            const assigned = receivedItems - installedItems;
            const installed = installedItems;
            const total = receivedItems;

            return {
              jobId: job.id,
              stats: {
                totalItems: total,
                assignedItems: assigned,
                installedItems: installed,
                plannedItems: plannedItems,
                hasStockShortage: hasStockShortage,
                installationProgress: total > 0 ? Math.round((installed / total) * 100) : 0
              }
            };
          } catch (error) {
            return {
              jobId: job.id,
              stats: {
                totalItems: 0,
                assignedItems: 0,
                installedItems: 0,
                plannedItems: 0,
                hasStockShortage: false,
                installationProgress: 0
              }
            };
          }
        });

        const results = await Promise.all(statsPromises);
        const statsMap = {};
        results.forEach(result => {
          statsMap[result.jobId] = result.stats;
        });

        setJobEquipmentStats(statsMap);
      }
    } catch (error) {
      console.error('Error loading job equipment stats:', error);
      // Continue without stats rather than breaking the page
      setJobEquipmentStats({});
    } finally {
      setStatsLoading(false);
    }
  }, [jobList]);

  // Get equipment stats for a specific job
  const getJobEquipmentStats = (job) => {
    return jobEquipmentStats[job.id] || {
      totalItems: 0,
      assignedItems: 0,
      installedItems: 0,
      plannedItems: 0,
      hasStockShortage: false,
      installationProgress: 0
    };
  };

  // Get job phase based on equipment status
  const getJobPhase = (planned, received, hasStockShortage, installed = 0) => {
    if (planned === 0) return 'Needs Planning';
    if (received === 0) return 'Awaiting Equipment';
    if (received < planned || hasStockShortage) return 'Receiving Equipment';
    // If any items are installed/checked out, mark as "In Progress"
    if (installed > 0) return 'In Progress';
    if (received === planned && !hasStockShortage) return 'Ready to Install';
    return 'In Progress';
  };

  // Get job phase color
  const getJobPhaseColor = (planned, received, hasStockShortage) => {
    if (planned === 0) return 'error';
    if (received === 0) return 'warning';
    if (received < planned || hasStockShortage) return 'info';
    if (received === planned && !hasStockShortage) return 'success';
    return 'default';
  };

  // Get job stack location
  const getJobStackLocation = (jobIdOrJob) => {
    // Handle both job ID and job object
    let job;
    if (typeof jobIdOrJob === 'object' && jobIdOrJob !== null) {
      job = jobIdOrJob;
    } else {
      // Find the job by ID and return its stack location
      job = jobList.find(j => j.id === jobIdOrJob || j.jobNumber === jobIdOrJob);
    }
    
    // Check for new stackLocations format first
    if (job?.stackLocations && job.stackLocations.length > 0) {
      if (!allRacks || allRacks.length === 0 || !allBays || allBays.length === 0) {
        return 'Loading...';
      }
      
      const stackLabels = job.stackLocations.map(location => {
        const rack = allRacks.find(r => r.id === location.rackId);
        const bay = allBays.find(b => b.id === location.bayId);
        // Find warehouse that contains this rack
        const warehouse = allWarehouses.find(w =>
          w.racks && w.racks.some(wr => wr.id === location.rackId)
        );

        const warehouseName = warehouse?.name || '';
        const rackName = rack?.name || '?';
        const bayName = bay?.name || '?';

        // Format: Warehouse-Rack Bay (e.g., "Main-10A STACK")
        return warehouseName ? `${warehouseName}-${rackName} ${bayName}` : `${rackName} ${bayName}`;
      });
      
      return stackLabels.join(', ');
    }
    
    // Fallback to old format for backward compatibility
    if (job?.stackRackId) {
      // Look up warehouse and rack names
      const warehouse = allWarehouses.find(w => w.id === job.stackWarehouseId);
      const rack = allRacks.find(r => r.id === job.stackRackId);
      
      // Debug logging
      console.log('Stack location lookup for job:', job.id, {
        stackWarehouseId: job.stackWarehouseId,
        stackRackId: job.stackRackId,
        warehouse,
        rack,
        allWarehousesCount: allWarehouses.length,
        allRacksCount: allRacks.length
      });
      
      const bay = allBays.find(b => b.id === job.stackBayId);

      const parts = [];
      if (warehouse?.name) parts.push(warehouse.name);
      if (rack?.name) parts.push(rack.name);
      const location = parts.join('-');

      if (bay?.name) {
        return `${location} ${bay.name}`;
      } else if (location) {
        return location;
      }
      // Fallback to ID if names not found yet (data still loading)
      return job.stackLocation || 'Loading...';
    }
    
    return 'TBD';
  };

  // Note: We no longer need to load warehouses/racks/bays separately
  // They are provided by InventoryPageContext



  // Get status color (legacy - keeping for backward compatibility)
  const getStatusColor = (status) => {
    switch (status) {
      case 'Planning':
        return 'default';
      case 'Upcoming':
        return 'primary';
      case 'Active':
        return 'success';
      case 'Completed':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // Refresh the page
  const handleRefresh = async () => {
    await loadJobs();
    // Removed loadInventorySummary() to prevent unnecessary API calls
  };
  
  // Handle search query change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };
  
  // Handle status filter change
  const handleStatusFilterChange = (status) => {
    setStatusFilters(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };
  
  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilters({
      'Needs Planning': true,
      'Awaiting Equipment': true,
      'Receiving Equipment': true,
      'Ready to Install': true,
      'In Progress': true,
      'Completed': true
    });
    setDateFilter('all');
    setCustomDateRange({
      startDate: '',
      endDate: ''
    });
  };

  // Handle date filter change
  const handleDateFilterChange = (event, newValue) => {
    if (newValue !== null) {
      setDateFilter(newValue);
      if (newValue !== 'custom') {
        setCustomDateRange({ startDate: '', endDate: '' });
      }
    }
  };

  // Get date range for filter
  const getDateRange = (filterType) => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDate = today.getDate();

    switch (filterType) {
      case 'today':
        return {
          start: new Date(currentYear, currentMonth, currentDate),
          end: new Date(currentYear, currentMonth, currentDate)
        };
      case 'thisWeek':
        const weekStart = new Date(today);
        weekStart.setDate(currentDate - today.getDay());
        const weekEnd = new Date(today);
        weekEnd.setDate(currentDate - today.getDay() + 6);
        return { start: weekStart, end: weekEnd };
      case 'thisMonth':
        return {
          start: new Date(currentYear, currentMonth, 1),
          end: new Date(currentYear, currentMonth + 1, 0)
        };
      case 'custom':
        return {
          start: customDateRange.startDate ? new Date(customDateRange.startDate) : null,
          end: customDateRange.endDate ? new Date(customDateRange.endDate) : null
        };
      default:
        return null;
    }
  };

  // Check if job falls within date range
  const isJobInDateRange = (job) => {
    if (dateFilter === 'all') return true;
    
    const range = getDateRange(dateFilter);
    if (!range) return true;
    
    const jobStartDate = job.startDate ? new Date(job.startDate) : null;
    const jobEndDate = job.endDate ? new Date(job.endDate) : null;
    
    // If job has no dates, show it in all filters except custom with specific dates
    if (!jobStartDate && !jobEndDate) {
      return dateFilter !== 'custom' || (!range.start && !range.end);
    }
    
    // For custom range, both start and end must be specified
    if (dateFilter === 'custom' && (!range.start || !range.end)) {
      return true; // Show all jobs if custom range is incomplete
    }
    
    // Check if job dates overlap with filter range
    const checkDate = jobStartDate || jobEndDate;
    if (range.start && range.end) {
      return checkDate >= range.start && checkDate <= range.end;
    }
    
    return true;
  };
  
  // Toggle filters visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };
  
  // Toggle custom job ID mode
  const toggleCustomId = () => {
    setUseAutoId(!useAutoId);
    if (useAutoId) {
      // When switching to custom ID, focus the field
      setTimeout(() => {
        const idField = document.getElementById('customJobId');
        if (idField) idField.focus();
      }, 100);
    }
  };
  

  // Load equipment stats when job list changes
  useEffect(() => {
    loadJobEquipmentStats();
  }, [loadJobEquipmentStats]);

  // Apply filters to job list
  useEffect(() => {
    if (!jobList) {
      setFilteredJobList([]);
      return;
    }

    let filtered = [...jobList];

    // Add PM demo job if user has access
    if (hasPMAccess) {
      const pmDemoJob = {
        id: 'pm-demo-job',
        jobNumber: 'J2024-087',
        name: 'Project Management Demo - Noblesville High School',
        client: 'Noblesville Schools',
        location: 'Noblesville, IN',
        status: 'Active',
        startDate: '2024-09-15',
        endDate: '2024-10-30',
        notes: 'This is a demo job showcasing integrated project management features with equipment tracking.',
        sites: [
          { id: 'pm-site-1', name: 'Main Campus' }
        ],
        // PM-specific fields for demo
        budgetHours: 80,
        actualHours: 52,
        hasLift: true,
        liftHeight: '35 ft',
        pmName: 'Matt',
        pmColor: '#2196f3'
      };

      // Only add if not already present (to avoid duplicates)
      if (!filtered.find(j => j.id === 'pm-demo-job')) {
        filtered = [pmDemoJob, ...filtered];
      }
    }

    // Apply equipment phase filters
    filtered = filtered.filter(job => {
      // Handle "Completed" status separately since it's stored in database
      if (job.status === 'Completed') {
        return statusFilters['Completed'] === true;
      }

      // For all other jobs, calculate equipment phase
      const stats = getJobEquipmentStats(job);
      const phase = getJobPhase(stats.plannedItems, stats.totalItems, stats.hasStockShortage, stats.installedItems);
      return statusFilters[phase] === true;
    });
    
    // Apply date filters
    filtered = filtered.filter(job => isJobInDateRange(job));
    
    // Apply search query (local filtering for immediate feedback)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(job =>
        job.name.toLowerCase().includes(query) ||
        job.client.toLowerCase().includes(query) ||
        job.id.toLowerCase().includes(query) ||
        (job.jobNumber && job.jobNumber.toLowerCase().includes(query)) ||
        (job.location && job.location.toLowerCase().includes(query))
      );
    }

    setFilteredJobList(filtered);
  }, [jobList, searchQuery, statusFilters, dateFilter, customDateRange, hasPMAccess]);

  // Note: Server-side search removed since we now load all jobs (limit=500)
  // Local filtering in useMemo above handles search across all jobs

  // Save filter settings to localStorage whenever they change
  useEffect(() => {
    const filterSettings = {
      searchQuery,
      statusFilters,
      dateFilter,
      customDateRange,
      showFilters
    };
    localStorage.setItem('jobsPageFilters', JSON.stringify(filterSettings));
  }, [searchQuery, statusFilters, dateFilter, customDateRange, showFilters]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Jobs
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          New Job
        </Button>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl variant="outlined" fullWidth>
              <OutlinedInput
                placeholder="Search jobs by name, client, or ID..."
                value={searchQuery}
                onChange={handleSearchChange}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                }
                endAdornment={
                  searchQuery && (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setSearchQuery('')} edge="end" size="small">
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }
              />
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                startIcon={<FilterIcon />}
                variant="outlined"
                onClick={toggleFilters}
                color="primary"
              >
                Filters
              </Button>
              
              {(searchQuery || !Object.values(statusFilters).every(v => v === true) || dateFilter !== 'all') && (
                <Button
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  variant="outlined"
                  color="secondary"
                >
                  Clear
                </Button>
              )}
              
              <Tooltip title="Refresh jobs">
                <IconButton onClick={handleRefresh} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
          
          {/* Status and Date Filters */}
          <Grid item xs={12}>
            <Collapse in={showFilters}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={3}>
                  {/* Equipment Phase Filters */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Equipment Phase
                    </Typography>
                    <FormGroup row>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={statusFilters['Needs Planning']}
                            onChange={() => handleStatusFilterChange('Needs Planning')}
                          />
                        }
                        label={<Chip label="Needs Planning" size="small" color="error" />}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={statusFilters['Awaiting Equipment']}
                            onChange={() => handleStatusFilterChange('Awaiting Equipment')}
                          />
                        }
                        label={<Chip label="Awaiting Equipment" size="small" color="warning" />}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={statusFilters['Receiving Equipment']}
                            onChange={() => handleStatusFilterChange('Receiving Equipment')}
                          />
                        }
                        label={<Chip label="Receiving Equipment" size="small" color="info" />}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={statusFilters['Ready to Install']}
                            onChange={() => handleStatusFilterChange('Ready to Install')}
                          />
                        }
                        label={<Chip label="Ready to Install" size="small" color="success" />}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={statusFilters['In Progress']}
                            onChange={() => handleStatusFilterChange('In Progress')}
                          />
                        }
                        label={<Chip label="In Progress" size="small" color="default" />}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={statusFilters.Completed}
                            onChange={() => handleStatusFilterChange('Completed')}
                          />
                        }
                        label={<Chip label="Completed" size="small" color="secondary" />}
                      />
                    </FormGroup>
                  </Grid>

                  {/* Date Filters */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Date Range
                    </Typography>
                    <ToggleButtonGroup
                      value={dateFilter}
                      exclusive
                      onChange={handleDateFilterChange}
                      size="small"
                      sx={{ mb: 2 }}
                    >
                      <ToggleButton value="all">All</ToggleButton>
                      <ToggleButton value="today">Today</ToggleButton>
                      <ToggleButton value="thisWeek">This Week</ToggleButton>
                      <ToggleButton value="thisMonth">This Month</ToggleButton>
                      <ToggleButton value="custom">Custom</ToggleButton>
                    </ToggleButtonGroup>
                    
                    {dateFilter === 'custom' && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <TextField
                          type="date"
                          label="Start Date"
                          size="small"
                          value={customDateRange.startDate}
                          onChange={(e) => setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          type="date"
                          label="End Date"
                          size="small"
                          value={customDateRange.endDate}
                          onChange={(e) => setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Collapse>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : jobList.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No jobs found
          </Typography>
          <Typography color="text.secondary" paragraph>
            Create your first job to get started.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Create Job
          </Button>
        </Paper>
      ) : filteredJobList.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No jobs match your filters
          </Typography>
          <Typography color="text.secondary" paragraph>
            Try adjusting your search or filter criteria.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
        </Paper>
      ) : (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', 
          gap: 2,
          '@media (max-width: 1400px)': {
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))'
          },
          '@media (max-width: 1000px)': {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
          },
          '@media (max-width: 600px)': {
            gridTemplateColumns: 'minmax(0, 1fr)'
          }
        }}>
          {filteredJobList.map(job => {
            const { totalItems, assignedItems, installedItems, plannedItems, hasStockShortage, installationProgress } = getJobEquipmentStats(job);
            const siteCount = job.sites?.length || 0;
            
            return (
              <Box key={job.id} sx={{ minWidth: 0, width: '100%' }}>
                <Card
                  variant="elevation"
                  sx={{
                    position: 'relative',
                    bgcolor: job.id === 'pm-demo-job' ? 'primary.50' : 'background.paper',
                    height: '100%',
                    width: '100%', // Ensure card fills grid cell width
                    minWidth: 0,
                    maxWidth: '100%', // Prevent expansion beyond grid cell
                    overflow: 'hidden', // Hide overflow content
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: job.id === 'pm-demo-job' ? 3 : 1,
                    borderRadius: 1,
                    border: job.id === 'pm-demo-job' ? '2px solid' : 'none',
                    borderColor: job.id === 'pm-demo-job' ? 'primary.main' : 'transparent',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: job.id === 'pm-demo-job' ? 6 : 4,
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '5px',
                      background: job.id === 'pm-demo-job'
                        ? 'linear-gradient(135deg, #1976d2, #42a5f5)'
                        : 'linear-gradient(135deg, #2C2C2C, #4A4A4A)',
                    }
                  }}
                  onClick={() => {
                    if (job.id === 'pm-demo-job') {
                      navigate('/jobs/pm-demo-job');
                    } else {
                      navigate(`/jobs/${job.id}`);
                    }
                  }}
                >
                  <CardContent sx={{ 
                    flexGrow: 1, 
                    display: 'flex', 
                    flexDirection: 'column',
                    p: 1.5,
                    pb: 1
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                      <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" component="div" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }} noWrap>
                            {job.jobNumber || job.id}
                          </Typography>
                          {job.id === 'pm-demo-job' && (
                            <Chip
                              label="PM Demo"
                              size="small"
                              color="primary"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                        <Typography variant="body1" color="text.secondary" noWrap>
                          {job.name}
                        </Typography>
                      </Box>
                      <Chip
                        label={getJobPhase(plannedItems, totalItems, hasStockShortage, installedItems)}
                        size="small"
                        color={getJobPhaseColor(plannedItems, totalItems, hasStockShortage)}
                        sx={{ height: 22 }}
                      />
                    </Box>
                    
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {job.client}
                        </Typography>
                        {/* Priority indicator */}
                        {(job.priority === 'High' || job.priority === 'Urgent') && (
                          <Chip 
                            label={job.priority === 'Urgent' ? 'Urgent' : 'High Priority'} 
                            size="small" 
                            color="error" 
                            sx={{ fontSize: '0.65rem', height: 18 }}
                          />
                        )}
                      </Box>
                      
                      {job.location && (
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.9rem' }}>
                          {job.location}
                        </Typography>
                      )}
                      
                      {/* Equipment Progress */}
                      <Box sx={{ mt: 1, mb: 0.5 }}>
                        {statsLoading && !jobEquipmentStats[job.id] ? (
                          <>
                            <Skeleton variant="text" width="70%" height={20} />
                            <Skeleton variant="rectangular" height={6} sx={{ borderRadius: 3, mt: 0.5 }} />
                          </>
                        ) : (
                          <>
                            <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {totalItems}/{plannedItems} Equipment Received
                            </Typography>
                            {plannedItems > 0 && (
                              <LinearProgress
                                variant="determinate"
                                value={(totalItems / plannedItems) * 100}
                                sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                                color={totalItems === plannedItems ? 'success' : 'primary'}
                              />
                            )}
                            {plannedItems === 0 && (
                              <LinearProgress
                                variant="determinate"
                                value={0}
                                sx={{ height: 6, borderRadius: 3, mt: 0.5, opacity: 0.3 }}
                                color="warning"
                              />
                            )}
                            {installedItems > 0 && (
                              <Typography variant="caption" color="success.main" sx={{ fontSize: '0.8rem', mt: 0.5, display: 'block' }}>
                                {installedItems} items installed
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                        <Typography variant="caption" color={siteCount > 0 ? "primary" : "text.secondary"} sx={{ display: 'flex', alignItems: 'center', fontSize: '0.85rem' }}>
                          <LocationIcon sx={{ fontSize: 16, mr: 0.25 }} />
                          {siteCount} {siteCount === 1 ? 'Site' : 'Sites'}
                        </Typography>
                        
                        {/* Subtle Stack Location */}
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                          Stack: {getJobStackLocation(job)}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {(job.startDate || job.endDate) && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.85rem', mt: 0.5 }}>
                        {job.startDate && `Start: ${job.startDate}`}
                        {job.startDate && job.endDate && ' • '}
                        {job.endDate && `End: ${job.endDate}`}
                      </Typography>
                    )}
                  </CardContent>
                  
                  <CardActions sx={{ justifyContent: 'flex-end', p: 1, gap: 1 }}>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<EditIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEditDialog(job);
                      }}
                      sx={{ fontSize: '0.9rem' }}
                    >
                      Edit
                    </Button>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDeleteConfirm(job);
                      }}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                </Card>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Recent Activity Card */}
      <Box sx={{ mt: 3 }}>
        <RecentActivity limit={10} showHeader={true} autoRefresh={true} />
      </Box>

      {/* Create/Edit Job Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create New Job' : 'Edit Job'}
        </DialogTitle>
        <DialogContent>
          {/* Basic Information */}
          <Box component="form" sx={{ mt: 1 }}>
              {dialogMode === 'create' ? (
                <Box mb={2}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8}>
                      <TextField
                        margin="normal"
                        fullWidth
                        id="customJobId"
                        label="Job ID"
                        name="customJobId"
                        value={jobDetails.customJobId}
                        onChange={handleInputChange}
                        disabled={useAutoId || dialogMode === 'edit'}
                        helperText={
                          idError ? idError : 
                          useAutoId ? `Auto-generated: ${generatePreviewId()}` : 
                          "Custom job ID (leave blank for auto-generated)"
                        }
                        error={!!idError}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={useAutoId}
                            onChange={toggleCustomId}
                            disabled={dialogMode === 'edit'}
                          />
                        }
                        label="Auto-generate ID"
                      />
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <TextField
                  margin="normal"
                  fullWidth
                  id="customJobId"
                  name="customJobId"
                  label="Job ID"
                  value={jobDetails.customJobId}
                  onChange={handleInputChange}
                  error={!!idError}
                  helperText={idError || "Edit job ID (must be unique)"}
                />
              )}
              
              <TextField
                margin="normal"
                required
                fullWidth
                id="name"
                label="Job Name"
                name="name"
                value={jobDetails.name}
                onChange={handleInputChange}
                autoFocus={dialogMode === 'create' ? false : true}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="client"
                label="Client"
                name="client"
                value={jobDetails.client}
                onChange={handleInputChange}
              />
              <TextField
                margin="normal"
                fullWidth
                id="location"
                label="Location"
                name="location"
                value={jobDetails.location}
                onChange={handleInputChange}
              />
              
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  fullWidth
                  id="startDate"
                  label="Start Date"
                  name="startDate"
                  type="date"
                  value={jobDetails.startDate}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  id="endDate"
                  label="End Date"
                  name="endDate"
                  type="date"
                  value={jobDetails.endDate}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <FormControl fullWidth>
                  <InputLabel id="status-label">Equipment Phase (Auto-Calculated)</InputLabel>
                  <TextField
                    id="equipment-phase"
                    label="Equipment Phase (Auto-Calculated)"
                    value={(() => {
                      const stats = getJobEquipmentStats(jobDetails);
                      return getJobPhase(
                        stats.plannedItems,
                        stats.assignedItems,
                        stats.hasStockShortage,
                        stats.installedItems
                      );
                    })()}
                    InputProps={{
                      readOnly: true,
                    }}
                    disabled
                    fullWidth
                    helperText="Equipment phase is automatically calculated based on equipment planning and assignments"
                  />
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel id="completion-label">Job Completion</InputLabel>
                  <Select
                    labelId="completion-label"
                    id="completion-status"
                    name="status"
                    value={jobDetails.status === 'Completed' ? 'Completed' : 'Active'}
                    label="Job Completion"
                    onChange={handleInputChange}
                  >
                    <MenuItem value="Active">Active (In Progress)</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                  </Select>
                  <FormHelperText>
                    Mark as "Completed" when all work is finished. Equipment phases are automatic.
                  </FormHelperText>
                </FormControl>
                
                <FormControl fullWidth>
                  <InputLabel id="priority-label">Priority</InputLabel>
                  <Select
                    labelId="priority-label"
                    id="priority"
                    name="priority"
                    value={jobDetails.priority}
                    label="Priority"
                    onChange={handleInputChange}
                  >
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Normal">Normal</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                    <MenuItem value="Urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              {/* Stack Location Selection - Multiple */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Stack Locations (Optional)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Add multiple warehouse, rack, and bay combinations for this job's stack. Examples: Warehouse A - Rack 3 - Bay A
                </Typography>

                {/* Existing Stack Locations */}
                {stackLocations.map((location, index) => {
                  const bay = allBays.find(b => b.id === location.bayId);

                  return (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', maxWidth: '100%' }}>
                        {/* Warehouse Selection - Editable to filter racks */}
                        <FormControl sx={{ flex: 1, minWidth: 200, maxWidth: 300 }} size="small">
                          <InputLabel>Warehouse</InputLabel>
                          <Select
                            value={location.warehouseId || ''}
                            label="Warehouse"
                            onChange={(e) => {
                              const updatedLocations = [...stackLocations];
                              updatedLocations[index] = { ...updatedLocations[index], warehouseId: e.target.value };
                              setStackLocations(updatedLocations);
                            }}
                          >
                            <MenuItem value="">
                              <em>All Warehouses</em>
                            </MenuItem>
                            {allWarehouses.map((w) => (
                              <MenuItem key={w.id} value={w.id}>
                                {w.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Rack Selection - Filtered by warehouse */}
                        <FormControl sx={{ flex: 1, minWidth: 120, maxWidth: 180 }} size="small">
                          <InputLabel>Rack</InputLabel>
                          <Select
                            value={location.rackId || ''}
                            label="Rack"
                            onChange={(e) => {
                              const newRackId = e.target.value;
                              const newRack = allRacks.find(r => r.id === newRackId);
                              const updatedLocations = [...stackLocations];
                              updatedLocations[index] = { ...updatedLocations[index], rackId: newRackId, bayId: '' };
                              setStackLocations(updatedLocations);
                              // Racks are independent of warehouses
                            }}
                          >
                            <MenuItem value="">
                              <em>Select rack...</em>
                            </MenuItem>
                            {allRacks
                              .sort((a, b) => {
                                const aNum = parseInt(a.name.match(/\d+/)?.[0] || '0');
                                const bNum = parseInt(b.name.match(/\d+/)?.[0] || '0');
                                return aNum - bNum;
                              })
                              .map((r) => (
                                <MenuItem key={r.id} value={r.id}>
                                  {r.name}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>

                        {/* Bay Selection - Editable */}
                        <FormControl sx={{ flex: 1, minWidth: 120, maxWidth: 180 }} size="small">
                          <InputLabel>Bay</InputLabel>
                          <Select
                            value={location.bayId || ''}
                            label="Bay"
                            onChange={(e) => {
                              const newBayId = e.target.value;
                              const updatedLocations = [...stackLocations];
                              updatedLocations[index] = { ...updatedLocations[index], bayId: newBayId };
                              setStackLocations(updatedLocations);
                            }}
                            disabled={!location.rackId}
                          >
                            <MenuItem value="">
                              <em>Select bay...</em>
                            </MenuItem>
                            {allBays.map((b) => (
                              <MenuItem key={b.id} value={b.id}>
                                {b.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Delete Button */}
                        <IconButton
                          color="error"
                          onClick={() => {
                            setStackLocations(stackLocations.filter((_, i) => i !== index));
                          }}
                          sx={{ minWidth: 48 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}

                {/* Add button to show new location form - Only show when there are existing locations and no form is open */}
                {stackLocations.length > 0 && !showAddLocationForm && (
                  <Button
                    variant="text"
                    onClick={() => {
                      setShowAddLocationForm(true);
                      setSelectedWarehouse('');
                      setSelectedRack('');
                      setSelectedBay('');
                    }}
                    startIcon={<AddIcon />}
                    size="small"
                    sx={{ mt: 1, color: 'text.secondary' }}
                  >
                    Add Location
                  </Button>
                )}

                {/* Add New Location Form - Only show when no existing locations OR actively adding */}
                {(stackLocations.length === 0 || showAddLocationForm) && (
                  <Box sx={{
                    mt: stackLocations.length > 0 ? 1 : 0,
                    pt: stackLocations.length > 0 ? 2 : 0,
                    borderTop: stackLocations.length > 0 ? '1px solid #e0e0e0' : 'none'
                  }}>
                    {stackLocations.length > 0 && (
                      <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                        Add another location:
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', maxWidth: '100%' }}>
                      {/* Warehouse Selection */}
                      <FormControl sx={{ flex: 1, minWidth: 200, maxWidth: 300 }} size="small">
                        <InputLabel id="new-warehouse-label">Warehouse</InputLabel>
                        <Select
                          labelId="new-warehouse-label"
                          value={selectedWarehouse}
                          label="Warehouse"
                          onChange={(e) => {
                            setSelectedWarehouse(e.target.value);
                            setSelectedRack(''); // Reset rack when warehouse changes
                            setSelectedBay('');  // Reset bay when warehouse changes
                          }}
                        >
                          <MenuItem value="">
                            <em>All Warehouses</em>
                          </MenuItem>
                          {allWarehouses.map((warehouse) => (
                            <MenuItem key={warehouse.id} value={warehouse.id}>
                              {warehouse.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Rack Selection - Filtered by selected warehouse */}
                      <FormControl sx={{ flex: 1, minWidth: 120, maxWidth: 180 }} size="small">
                        <InputLabel id="new-rack-label">Rack</InputLabel>
                        <Select
                          labelId="new-rack-label"
                          value={selectedRack}
                          onChange={(e) => {
                            const rackId = e.target.value;
                            setSelectedRack(rackId);
                            // Racks are independent of warehouses
                          }}
                          label="Rack"
                        >
                          <MenuItem value="">
                            <em>Select rack...</em>
                          </MenuItem>
                          {allRacks
                            .sort((a, b) => {
                              // Extract numbers from rack names for numerical sorting
                              const aNum = parseInt(a.name.match(/\d+/)?.[0] || '0');
                              const bNum = parseInt(b.name.match(/\d+/)?.[0] || '0');
                              return aNum - bNum;
                            })
                            .map((rack) => (
                              <MenuItem key={rack.id} value={rack.id}>
                                {rack.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>

                      {/* Bay Selection */}
                      <FormControl sx={{ flex: 1, minWidth: 120, maxWidth: 180 }} size="small">
                        <InputLabel id="new-bay-label">Bay</InputLabel>
                        <Select
                          labelId="new-bay-label"
                          value={selectedBay}
                          onChange={(e) => setSelectedBay(e.target.value)}
                          label="Bay"
                          disabled={!selectedRack}
                        >
                          <MenuItem value="">
                            <em>Select bay...</em>
                          </MenuItem>
                          {allBays.map((bay) => (
                            <MenuItem key={bay.id} value={bay.id}>
                              {bay.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Add Button */}
                      <Button
                        variant="outlined"
                        onClick={() => {
                          if (selectedRack && selectedBay) {
                            setStackLocations([...stackLocations, {
                              warehouseId: selectedWarehouse || '',
                              rackId: selectedRack,
                              bayId: selectedBay
                            }]);
                            setSelectedWarehouse('');
                            setSelectedRack('');
                            setSelectedBay('');
                            setShowAddLocationForm(false); // Hide the form after adding
                          }
                        }}
                        disabled={!selectedRack || !selectedBay}
                        startIcon={<AddIcon />}
                        size="small"
                        sx={{ minWidth: 100 }}
                      >
                        Add
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>

              <TextField
                margin="normal"
                fullWidth
                id="notes"
                label="Notes"
                name="notes"
                value={jobDetails.notes}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveJob}
            variant="contained"
            color="primary"
            disabled={!jobDetails.name || !jobDetails.client || dialogLoading}
          >
            {dialogLoading ? (
              <CircularProgress size={24} />
            ) : dialogMode === 'create' ? (
              'Create Job'
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onClose={handleCloseDeleteConfirm}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the job "{jobToDelete?.name}"?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm}>Cancel</Button>
          <Button onClick={handleDeleteJob} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}